import React from 'react';
import { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    TextField,
    Autocomplete,
    Button,
    IconButton,
    Divider,
    InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import supabase from '../../../supabaseClient';
import { FormControl, Select, MenuItem } from '@mui/material';
import { CURRENCY_OPTIONS, CURRENCY_SYMBOLS, formatCurrency } from './currency';

const emptyItem = () => ({
    key: crypto.randomUUID(),
    product_id: null,
    product_name: '',
    amount: '',
    unit_price: '',
    min_price: null,
    stock_quantity: null,
});

// Funkcja pomocnicza do obliczania tygodnia ISO roku
const getWeekNumber = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

function AddOrders({ open, onClose, onOrderCreated, editOrderId = null }) {
    const [kontrahenci, setKontrahenci] = useState([]);
    const [produkty, setProdukty] = useState([]);

    const [selectedClient, setSelectedClient] = useState(null);
    const [items, setItems] = useState([emptyItem()]);
    const [note, setNote] = useState('');
    const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
    const [planningOrderDate, setPlanningOrderDate] = useState(''); // Nowe pole: planowana data dostawy

    const [showNewClientForm, setShowNewClientForm] = useState(false);
    const [newClientName, setNewClientName] = useState('');
    const [newClientNip, setNewClientNip] = useState('');

    const [newProductRowKey, setNewProductRowKey] = useState(null);
    const [newProductName, setNewProductName] = useState('');
    const [newProductPrice, setNewProductPrice] = useState('');
    const [newProductStock, setNewProductStock] = useState('');

    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const [idConfiguration, setIdConfiguration] = useState(null);

    const [waluta, setWaluta] = useState('PLN');
    const handleChange = (event) => {
        setWaluta(event.target.value);
    };
    const currencySymbol = CURRENCY_SYMBOLS[waluta];

    // Pobranie id_configuration zalogowanego użytkownika z jego profilu
    useEffect(() => {
        const getInitialData = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('id_configuration')
                    .eq('id', session.user.id)
                    .single();

                if (profileData) {
                    setIdConfiguration(profileData.id_configuration);
                }
            }
        };
        getInitialData();
    }, []);

    // Pobranie kontrahentów i produktów (uwzględniając min_price) przy otwarciu modala
    useEffect(() => {
        if (!open || !idConfiguration) return;

        async function fetchOptions() {
            const [kontrahenciRes, produktyRes] = await Promise.all([
                supabase.from('kontrahenci').select('id, name').eq('id_configuration', idConfiguration).order('name'),
                supabase.from('products').select('id, name, default_price, min_price, stock_quantity').eq('id_configuration', idConfiguration).eq('type', 'produkt').order('name'),
            ]);

            if (kontrahenciRes.error) console.error('Błąd pobierania kontrahentów:', kontrahenciRes.error);
            if (produktyRes.error) console.error('Błąd pobierania produktów:', produktyRes.error);

            setKontrahenci(kontrahenciRes.data ?? []);
            setProdukty(produktyRes.data ?? []);
        }

        fetchOptions();
    }, [open, idConfiguration]);

    // Tryb edycji: doładowanie istniejącego zamówienia
    useEffect(() => {
        if (!open || !editOrderId || !idConfiguration || kontrahenci.length === 0 || produkty.length === 0) return;

        async function fetchOrderToEdit() {
            const { data, error } = await supabase
                .from('orders')
                .select(`
                    client_id,
                    note,
                    order_date,
                    planning_order_date,
                    currency,
                    order_items ( product, amount, unit_price, product_id )
                `)
                .eq('id', editOrderId)
                .single();

            if (error) {
                console.error('Błąd pobierania zamówienia do edycji:', error);
                setErrorMsg('Nie udało się wczytać zamówienia do edycji.');
                return;
            }

            const matchedClient = kontrahenci.find((k) => k.id === data.client_id) ?? null;
            setSelectedClient(matchedClient);

            setItems(
                data.order_items.map((item) => {
                    const matchedProduct = produkty.find((p) => p.id === item.product_id);
                    return {
                        key: crypto.randomUUID(),
                        product_id: item.product_id,
                        product_name: item.product,
                        amount: String(item.amount),
                        unit_price: String(item.unit_price),
                        min_price: matchedProduct ? matchedProduct.min_price : null,
                        stock_quantity: matchedProduct ? matchedProduct.stock_quantity : null,
                    };
                })
            );

            setNote(data.note ?? '');
            setOrderDate(data.order_date ?? new Date().toISOString().slice(0, 10));
            setPlanningOrderDate(data.planning_order_date ?? '');
            setWaluta(data.currency ?? 'PLN');
        }

        fetchOrderToEdit();
    }, [open, editOrderId, idConfiguration, kontrahenci, produkty]);

    // Reset stanu formularza po zamknięciu modala
    useEffect(() => {
        if (!open) {
            setSelectedClient(null);
            setItems([emptyItem()]);
            setNote('');
            setOrderDate(new Date().toISOString().slice(0, 10));
            setPlanningOrderDate('');
            setShowNewClientForm(false);
            setNewClientName('');
            setNewClientNip('');
            setNewProductRowKey(null);
            setNewProductName('');
            setNewProductPrice('');
            setNewProductStock('');
            setErrorMsg('');
            setWaluta('PLN');
        }
    }, [open]);

    const handleAddItem = () => {
        setItems((prev) => [...prev, emptyItem()]);
    };

    const handleRemoveItem = (key) => {
        setItems((prev) => (prev.length > 1 ? prev.filter((item) => item.key !== key) : prev));
    };

    const handleSelectProduct = (key, product) => {
        setItems((prev) =>
            prev.map((item) =>
                item.key === key
                    ? {
                          ...item,
                          product_id: product?.id ?? null,
                          product_name: product?.name ?? '',
                          unit_price: product ? String(product.default_price) : '',
                          min_price: product?.min_price ?? null,
                          stock_quantity: product ? product.stock_quantity : null,
                      }
                    : item
            )
        );
    };

    const handleItemFieldChange = (key, field, value) => {
        setItems((prev) => prev.map((item) => (item.key === key ? { ...item, [field]: value } : item)));
    };

    const handleCreateClient = async () => {
        if (!newClientName.trim()) return;

        const { data, error } = await supabase
            .from('kontrahenci')
            .insert({ name: newClientName.trim(), nip: newClientNip.trim() || null, id_configuration: idConfiguration })
            .select()
            .single();

        if (error) {
            console.error('Błąd dodawania kontrahenta:', error);
            setErrorMsg('Nie udało się dodać kontrahenta.');
            return;
        }

        setKontrahenci((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
        setSelectedClient(data);
        setShowNewClientForm(false);
        setNewClientName('');
        setNewClientNip('');
    };

    const handleCreateProduct = async (rowKey) => {
        if (!newProductName.trim()) return;

        const { data, error } = await supabase
            .from('products')
            .insert({
                name: newProductName.trim(),
                default_price: Number(newProductPrice) || 0,
                stock_quantity: Number(newProductStock) || 0,
                id_configuration: idConfiguration,
            })
            .select()
            .single();

        if (error) {
            console.error('Błąd dodawania produktu:', error);
            setErrorMsg('Nie udało się dodać produktu.');
            return;
        }

        setProdukty((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
        handleSelectProduct(rowKey, data);
        setNewProductRowKey(null);
        setNewProductName('');
        setNewProductPrice('');
        setNewProductStock('');
    };

    const handleSubmit = async () => {
        if (!idConfiguration || !selectedClient) {
            setErrorMsg('Uzupełnij kontrahenta oraz wszystkie pozycje zamówienia.');
            return;
        }

        // Walidacja poprawności danych podstawowych
        for (const item of items) {
            if (!item.product_id || Number(item.amount) <= 0 || Number(item.unit_price) < 0) {
                setErrorMsg('Uzupełnij wszystkie pozycje zamówienia (produkt, prawidłowa ilość i cena).');
                return;
            }

            // Sprawdzenie, czy cena nie jest mniejsza od minimalnej z kartoteki produktu
            if (item.min_price !== null && Number(item.unit_price) < Number(item.min_price)) {
                setErrorMsg(`Cena dla produktu "${item.product_name || 'Wybrany produkt'}" jest mniejsza od dopuszczalnej ceny minimalnej (${formatCurrency(item.min_price, waluta)}).`);
                return;
            }
        }

        setSubmitting(true);
        setErrorMsg('');

        const totalPrice = items.reduce((sum, item) => sum + Number(item.amount) * Number(item.unit_price), 0);

        let orderId = editOrderId;

        if (editOrderId) {
            const { error: orderError } = await supabase
                .from('orders')
                .update({
                    client_id: selectedClient.id,
                    order_date: orderDate,
                    planning_order_date: planningOrderDate || null,
                    total_price: totalPrice,
                    note: note.trim() || null,
                    currency: waluta,
                })
                .eq('id', editOrderId);

            if (orderError) {
                console.error('Błąd aktualizacji zamówienia:', orderError);
                setErrorMsg('Nie udało się zapisać zmian w zamówieniu.');
                setSubmitting(false);
                return;
            }

            const { error: deleteItemsError } = await supabase.from('order_items').delete().eq('order_id', editOrderId);

            if (deleteItemsError) {
                console.error('Błąd czyszczenia pozycji zamówienia:', deleteItemsError);
                setErrorMsg('Zamówienie zaktualizowane, ale nie udało się zapisać pozycji.');
                setSubmitting(false);
                return;
            }
        } else {
            const { data: userData } = await supabase.auth.getUser();

            const { data: order, error: orderError } = await supabase
                .from('orders')
                .insert({
                    client_id: selectedClient.id,
                    order_date: orderDate,
                    planning_order_date: planningOrderDate || null,
                    realizacja: 'nowe',
                    total_price: totalPrice,
                    note: note.trim() || null,
                    id_configuration: idConfiguration,
                    owner: userData?.user?.id ?? null,
                    currency: waluta,
                })
                .select()
                .single();

            if (orderError) {
                console.error('Błąd tworzenia zamówienia:', orderError);
                setErrorMsg('Nie udało się utworzyć zamówienia.');
                setSubmitting(false);
                return;
            }

            orderId = order.id;
        }

        const orderItemsPayload = items.map((item) => ({
            order_id: orderId,
            product: item.product_name,
            product_id: item.product_id,
            amount: Number(item.amount),
            unit_price: Number(item.unit_price),
            total_price: Number(item.amount) * Number(item.unit_price),
            id_configuration: idConfiguration,
        }));

        const { error: itemsError } = await supabase.from('order_items').insert(orderItemsPayload);

        if (itemsError) {
            console.error('Błąd zapisu pozycji zamówienia:', itemsError);
            setErrorMsg('Zamówienie zapisane, ale nie udało się zapisać pozycji.');
            setSubmitting(false);
            return;
        }

        setSubmitting(false);
        onOrderCreated?.();
        onClose();
    };

    const planningWeek = getWeekNumber(planningOrderDate);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
            <Box sx={{ bgcolor: '#EDF2F7', px: 4, pt: 3, pb: 3 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.05em', color: 'text.secondary', mb: 0.5 }}>
                    ZAMÓWIENIE
                </Typography>
                <Typography sx={{ fontSize: '26px', fontWeight: 700, color: '#3D6E9E' }}>
                    {editOrderId ? 'Edycja zamówienia' : 'Nowe zamówienie'}
                </Typography>
            </Box>

            <DialogContent sx={{ p: 4, maxHeight: '65vh', overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>Data zamówienia</Typography>
                        <TextField
                            type="date"
                            size="small"
                            fullWidth
                            value={orderDate}
                            onChange={(e) => setOrderDate(e.target.value)}
                        />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>
                            Planowana data dostawy {planningWeek ? `(tydzień ${planningWeek})` : ''}
                        </Typography>
                        <TextField
                            type="date"
                            size="small"
                            fullWidth
                            value={planningOrderDate}
                            onChange={(e) => setPlanningOrderDate(e.target.value)}
                        />
                    </Box>
                    <Box sx={{ flex: '0 0 140px' }}>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>Waluta</Typography>
                        <FormControl fullWidth size="small">
                            <Select value={waluta} onChange={handleChange}>
                                {CURRENCY_OPTIONS.map((code) => (
                                    <MenuItem key={code} value={code}>{code}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>Kontrahent</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Autocomplete
                            sx={{ flex: 1 }}
                            options={kontrahenci}
                            getOptionLabel={(option) => option.name ?? ''}
                            value={selectedClient}
                            onChange={(_, value) => setSelectedClient(value)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderInput={(params) => <TextField {...params} placeholder="Wybierz kontrahenta" size="small" />}
                        />
                        <Button
                            variant="outlined"
                            onClick={() => setShowNewClientForm((prev) => !prev)}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', whiteSpace: 'nowrap' }}
                        >
                            + Nowy
                        </Button>
                    </Box>
                </Box>

                {showNewClientForm && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, p: 2, bgcolor: '#FAF7F2', borderRadius: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Nazwa kontrahenta"
                            value={newClientName}
                            onChange={(e) => setNewClientName(e.target.value)}
                            sx={{ flex: 1.4 }}
                        />
                        <TextField
                            size="small"
                            placeholder="NIP (opcjonalnie)"
                            value={newClientNip}
                            onChange={(e) => setNewClientNip(e.target.value)}
                            sx={{ flex: 1 }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleCreateClient}
                            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
                        >
                            Zapisz
                        </Button>
                    </Box>
                )}

                <Divider sx={{ my: 2.5 }} />

                <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 1 }}>Pozycje zamówienia</Typography>

                {items.map((item) => {
                    const value = (Number(item.amount) || 0) * (Number(item.unit_price) || 0);
                    const isBelowMin = item.min_price !== null && Number(item.unit_price) < Number(item.min_price);

                    return (
                        <Box
                            key={item.key}
                            sx={{
                                border: '1px solid #ECE7DE',
                                borderRadius: 2,
                                px: 2.5,
                                py: 2,
                                mb: 1.5,
                            }}
                        >
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1.5 }}>
                                <Autocomplete
                                    sx={{ flex: 1 }}
                                    options={produkty}
                                    getOptionLabel={(option) => option.name ?? ''}
                                    value={produkty.find((p) => p.id === item.product_id) ?? null}
                                    onChange={(_, value) => handleSelectProduct(item.key, value)}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Wybierz produkt" size="small" />
                                    )}
                                />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() =>
                                        setNewProductRowKey((prev) => (prev === item.key ? null : item.key))
                                    }
                                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', whiteSpace: 'nowrap' }}
                                >
                                    + Nowy
                                </Button>
                                <IconButton size="small" onClick={() => handleRemoveItem(item.key)}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>

                            {newProductRowKey === item.key && (
                                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, p: 1.5, bgcolor: '#FAF7F2', borderRadius: 2 }}>
                                    <TextField
                                        size="small"
                                        placeholder="Nazwa produktu"
                                        value={newProductName}
                                        onChange={(e) => setNewProductName(e.target.value)}
                                        sx={{ flex: 1.4, bgcolor: 'background.paper' }}
                                    />
                                    <TextField
                                        size="small"
                                        placeholder="Cena"
                                        type="number"
                                        value={newProductPrice}
                                        onChange={(e) => setNewProductPrice(e.target.value)}
                                        InputProps={{ endAdornment: <InputAdornment position="end">{currencySymbol}</InputAdornment> }}
                                        sx={{ flex: 0.8, bgcolor: 'background.paper' }}
                                    />
                                    <TextField
                                        size="small"
                                        placeholder="Na stanie"
                                        type="number"
                                        value={newProductStock}
                                        onChange={(e) => setNewProductStock(e.target.value)}
                                        sx={{ flex: 0.8, bgcolor: 'background.paper' }}
                                    />
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleCreateProduct(item.key)}
                                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
                                    >
                                        Zapisz
                                    </Button>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3 }}>
                                <Box sx={{ flex: '0 0 130px' }}>
                                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.5 }}>
                                        Ilość
                                    </Typography>
                                    <TextField
                                        size="small"
                                        type="number"
                                        fullWidth
                                        value={item.amount}
                                        onChange={(e) => handleItemFieldChange(item.key, 'amount', e.target.value)}
                                    />
                                </Box>

                                <Box sx={{ flex: '0 0 170px' }}>
                                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.5 }}>
                                        Cena jednostkowa
                                    </Typography>
                                    <TextField
                                        size="small"
                                        type="number"
                                        fullWidth
                                        error={isBelowMin}
                                        value={item.unit_price}
                                        onChange={(e) => handleItemFieldChange(item.key, 'unit_price', e.target.value)}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">{currencySymbol}</InputAdornment>,
                                        }}
                                    />
                                </Box>

                                {item.stock_quantity !== null && (
                                    <Box sx={{ flex: '0 0 100px' }}>
                                        <Typography sx={{ fontSize: '11px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.5 }}>
                                            Dostępne
                                        </Typography>
                                        <Typography sx={{ fontWeight: 600, py: '8.5px' }}>{item.stock_quantity}</Typography>
                                    </Box>
                                )}

                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: '11px', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em', mb: 0.5 }}>
                                        Wartość
                                    </Typography>
                                    <Typography sx={{ fontWeight: 700, py: '8.5px' }}>
                                        {formatCurrency(value, waluta)}
                                    </Typography>
                                </Box>
                            </Box>

                            {isBelowMin && (
                                <Typography sx={{ color: '#C7433F', fontSize: '11px', mt: 1, fontWeight: 500 }}>
                                    Uwaga: Cena jest niższa niż cena minimalna w kartotece ({formatCurrency(item.min_price, waluta)})!
                                </Typography>
                            )}
                        </Box>
                    );
                })}

                <Button
                    fullWidth
                    onClick={handleAddItem}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        color: 'text.secondary',
                        border: '1px dashed #D8D2C7',
                        borderRadius: 2,
                        py: 1,
                        mb: 3,
                    }}
                >
                    + dodaj kolejny produkt
                </Button>

                <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
                    <Box>
                        <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>Wartość zamówienia</Typography>
                        <Typography sx={{ fontWeight: 700, fontSize: '18px' }}>
                            {formatCurrency(
                                items.reduce((sum, item) => sum + (Number(item.amount) || 0) * (Number(item.unit_price) || 0), 0),
                                waluta
                            )}
                        </Typography>
                    </Box>
                </Box>

                <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>Uwagi</Typography>
                <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Opcjonalnie"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    sx={{ mb: 3, bgcolor: '#FAF7F2', borderRadius: 2, '& fieldset': { border: 'none' } }}
                />

                {errorMsg && (
                    <Typography sx={{ color: '#C7433F', fontSize: '13px', mb: 1 }}>{errorMsg}</Typography>
                )}
            </DialogContent>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1.5,
                    px: 4,
                    py: 2.5,
                    borderTop: '1px solid #ECE7DE',
                    bgcolor: 'background.paper',
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={submitting}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}
                >
                    Anuluj
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}
                >
                    {submitting ? 'Zapisywanie...' : editOrderId ? 'Zapisz zmiany' : 'Zatwierdź zamówienie'}
                </Button>
            </Box>
        </Dialog>
    );
}

export default AddOrders;